/**
 * v5.5 — Lightweight i18n.
 *
 * Не тащим i18next/react-intl чтобы не раздувать bundle на 50KB ради
 * двух языков. Vanilla map + zustand store + один useT() hook.
 *
 * Расширение на новый язык:
 *   1. Создать `web/src/i18n/translations/<code>.ts` (mirror ru.ts).
 *   2. Импортировать здесь, добавить в LOCALES.
 *   3. (Опционально) Поменять <option> в LanguagePicker.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ru, type TranslationDict } from './translations/ru';
import { en } from './translations/en';

export type LocaleCode = 'ru' | 'en';

const LOCALES: Record<LocaleCode, TranslationDict> = { ru, en };

interface LocaleStore {
  locale: LocaleCode;
  setLocale: (l: LocaleCode) => void;
}

/**
 * Zustand store с persist в localStorage. По умолчанию определяем по
 * navigator.language (стартовый язык). На каждое изменение re-render
 * получает любой компонент, который читает useLocaleStore.
 */
export const useLocaleStore = create<LocaleStore>()(
  persist(
    (set) => ({
      locale: detectInitialLocale(),
      setLocale: (l) => {
        if (typeof document !== 'undefined') {
          document.documentElement.lang = l;
        }
        set({ locale: l });
      },
    }),
    { name: 'swipa-locale' },
  ),
);

function detectInitialLocale(): LocaleCode {
  if (typeof navigator === 'undefined') return 'ru';
  const lang = (navigator.language || '').toLowerCase();
  if (lang.startsWith('ru')) return 'ru';
  if (lang.startsWith('en')) return 'en';
  return 'ru'; // dev/прод default
}

/**
 * useT — hook для использования в компонентах.
 *
 * useT('settings.title')                       // 'Настройки'
 * useT('channel.joined', { name, channel })    // 'Иван присоединился к #general'
 *
 * Если ключа нет в текущей locale — fallback на ru. Если и там нет —
 * возвращаем сам ключ (видно в UI как пропущенный перевод).
 */
export function useT() {
  const locale = useLocaleStore((s) => s.locale);
  return (path: string, params?: Record<string, string | number>) => {
    return translate(locale, path, params);
  };
}

/**
 * Императивный helper (вне React render) — для error handlers, toast helpers,
 * email-builder'ов и прочего. Читает текущую locale напрямую.
 */
export function t(path: string, params?: Record<string, string | number>): string {
  return translate(useLocaleStore.getState().locale, path, params);
}

function translate(
  locale: LocaleCode,
  path: string,
  params?: Record<string, string | number>,
): string {
  const dictionary = LOCALES[locale] ?? ru;
  const value = lookup(dictionary, path) ?? lookup(ru, path) ?? path;
  if (typeof value !== 'string') return path;
  return params ? interpolate(value, params) : value;
}

function lookup(obj: unknown, path: string): unknown {
  const parts = path.split('.');
  let cur: any = obj;
  for (const p of parts) {
    if (cur && typeof cur === 'object' && p in cur) cur = cur[p];
    else return undefined;
  }
  return cur;
}

function interpolate(s: string, params: Record<string, string | number>): string {
  return s.replace(/\{\{(\w+)\}\}/g, (_m, k) => {
    const v = params[k];
    return v == null ? '' : String(v);
  });
}
